import { computed, unref } from "vue";
import useMagnoliaLanguage from "~/composables/useMagnoliaLanguage";
import { formatPercent } from "~/utils/formatPercent";

interface UseFormattedPercentOptions {
  locale?: string;
  precision?: number;
  showPositivePrefix?: boolean;
}

/**
 * A composable for formatting numbers as percentages with localization support.
 *
 * @param {number|string} value - The value to format as a percentage.
 * @param {UseFormattedPercentOptions} options - Formatting options.
 * @returns {object} - An object containing a computed property `formattedValue`.
 */
export function useFormattedPercent(
  value: number | string,
  options: UseFormattedPercentOptions = {},
) {
  const { currentLanguage } = useMagnoliaLanguage();

  // Resolve the 'locale' based on the provided options or Magnolia's current language
  const resolvedLocale = computed(() => options.locale || unref(currentLanguage) || "de-DE");

  // Compute the formatted value using the formatPercent utility function
  const formattedValue = computed(() =>
    formatPercent(unref(value), {
      locale: unref(resolvedLocale),
      precision: options.precision ?? 2,
      showPositivePrefix: options.showPositivePrefix ?? false,
    }),
  );

  return { formattedValue };
}
